/**
 * Locale primitives (Sprint-017.2 FR-1, ADR-0019).
 *
 * The framework distinguishes a *locale* from a *language*:
 *
 * - a locale is a BCP-47 tag describing cultural conventions (`fr-FR`, `pt-BR`);
 * - a language is its primary subtag (`fr`, `pt`), and is what translation
 *   resources are keyed by (`i18n:core/fr`).
 *
 * Keeping the region around matters for the locale-aware services planned for
 * later sprints: `fr-FR` and `fr-CA` share translations but not number, date or
 * measurement formatting.
 */
/** The language every installation is expected to provide (Sprint-017.2 FR-6). */
export const DEFAULT_LANGUAGE = 'en';
const LOCALE_PATTERN = /^([a-z]{2,3})(?:-([a-z]{4}))?(?:-([a-z]{2}|\d{3}))?$/i;
/**
 * Parses a BCP-47 tag, or returns `undefined` when it is not a language tag we
 * can act on. Subtags beyond language/script/region are ignored rather than
 * rejected, since they never affect translation selection.
 */
export function parseLocaleTag(tag) {
    const match = LOCALE_PATTERN.exec(tag.trim());
    if (match === null) {
        return undefined;
    }
    const [, language, script, region] = match;
    if (language === undefined) {
        return undefined;
    }
    return {
        language: language.toLowerCase(),
        script: script === undefined ? undefined : titleCase(script),
        region: region === undefined ? undefined : region.toUpperCase()
    };
}
/** Returns the canonical form of a tag (`FR-fr` -> `fr-FR`), or `undefined`. */
export function normalizeLocaleTag(tag) {
    const parsed = parseLocaleTag(tag);
    if (parsed === undefined) {
        return undefined;
    }
    return [parsed.language, parsed.script, parsed.region].filter(Boolean).join('-');
}
/** Returns the language subtag of a locale (`fr-FR` -> `fr`), or `undefined`. */
export function languageOf(tag) {
    return parseLocaleTag(tag)?.language;
}
export function isValidLocaleTag(tag) {
    return parseLocaleTag(tag) !== undefined;
}
/**
 * Picks the best available language for an ordered list of preferred locales
 * (Sprint-017.2 FR-1).
 *
 * Matching is on the language subtag, so `fr-FR` resolves to `fr`; an
 * unsupported preference such as `it-IT` falls through to the next preference
 * and ultimately to `fallback`. Returns `undefined` only when the fallback
 * itself is unavailable, which the caller reports as an unsupported locale.
 */
export function negotiateLanguage(preferred, available, fallback = DEFAULT_LANGUAGE) {
    const supported = new Set(available.map((language) => language.toLowerCase()));
    for (const candidate of preferred) {
        const language = languageOf(candidate);
        if (language !== undefined && supported.has(language)) {
            return language;
        }
    }
    const fallbackLanguage = languageOf(fallback);
    return fallbackLanguage !== undefined && supported.has(fallbackLanguage)
        ? fallbackLanguage
        : undefined;
}
/**
 * Human-readable name of a language, in that language ("français").
 *
 * Uses `Intl.DisplayNames` (ECMA-402), degrading to the code itself on runtimes
 * or tags it cannot resolve — locale metadata must never break the UI.
 */
export function languageDisplayName(language) {
    const DisplayNames = Intl.DisplayNames;
    if (DisplayNames === undefined) {
        return language;
    }
    try {
        return new DisplayNames([language], { type: 'language' }).of(language) ?? language;
    }
    catch {
        return language;
    }
}
function titleCase(value) {
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}
//# sourceMappingURL=locale.js.map